/**
 * AccountCard — displays a single account within the client detail page.
 *
 * Props: account (AccountRow — snake_case from API)
 *
 * Shows:
 *   - Account name and slug
 *   - Plan name (from plan_name join or plan_id fallback)
 *   - Status badge (color-coded)
 *   - Timezone
 *   - Deploy URL (external link) and deploy_status badge
 *   - Stripe customer ID or "Sin Stripe" placeholder
 */

import type { AccountRow } from '@1klick/types'

// ── Status badge config ───────────────────────────────────────────────────────

const ACCOUNT_STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  active: { color: 'bg-green-100 text-green-800', label: 'Activo' },
  pending: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendiente' },
  suspended: { color: 'bg-red-100 text-red-800', label: 'Suspendido' },
  inactive: { color: 'bg-gray-100 text-gray-600', label: 'Inactivo' },
}

const DEPLOY_STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pendiente' },
  success: { color: 'bg-green-100 text-green-700', label: 'Desplegado' },
  failed: { color: 'bg-red-100 text-red-700', label: 'Error' },
  retry: { color: 'bg-orange-100 text-orange-700', label: 'Reintentando' },
}

function AccountStatusBadge({ status }: { status: string }) {
  const config = ACCOUNT_STATUS_CONFIG[status] ?? {
    color: 'bg-gray-100 text-gray-600',
    label: status,
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.color}`}
    >
      {config.label}
    </span>
  )
}

function DeployStatusBadge({ status }: { status: string }) {
  const config = DEPLOY_STATUS_CONFIG[status] ?? {
    color: 'bg-gray-100 text-gray-600',
    label: status,
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${config.color}`}
    >
      {config.label}
    </span>
  )
}

// ── Component ─────────────────────────────────────────────────────────────────

interface AccountCardProps {
  account: AccountRow
}

export function AccountCard({ account }: AccountCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      {/* Header row */}
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-gray-900">{account.name}</h3>
          {account.slug && (
            <p className="mt-0.5 truncate font-mono text-xs text-gray-400">{account.slug}</p>
          )}
        </div>
        <AccountStatusBadge status={account.status} />
      </div>

      {/* Details grid */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        {/* Plan */}
        <div>
          <dt className="text-gray-500">Plan</dt>
          <dd className="mt-0.5 font-medium text-gray-700">
            {account.plan_name ?? (account.plan_id ? 'Plan asignado' : 'Sin plan')}
          </dd>
        </div>

        {/* Timezone */}
        <div>
          <dt className="text-gray-500">Zona horaria</dt>
          <dd className="mt-0.5 font-medium text-gray-700">{account.timezone}</dd>
        </div>

        {/* Deploy status */}
        <div>
          <dt className="text-gray-500">Estado deploy</dt>
          <dd className="mt-0.5">
            <DeployStatusBadge status={account.deploy_status} />
          </dd>
        </div>

        {/* Demo flag */}
        {account.demo_flag && (
          <div>
            <dt className="text-gray-500">Tipo</dt>
            <dd className="mt-0.5">
              <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">
                Demo
              </span>
            </dd>
          </div>
        )}
      </dl>

      {/* Deploy URL */}
      {account.deploy_url && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <a
            href={account.deploy_url}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            {account.deploy_url}
          </a>
        </div>
      )}

      {/* Stripe customer */}
      <div className="mt-2">
        <span className="text-xs text-gray-400">
          {account.stripe_customer_id ? (
            <span className="font-mono">{account.stripe_customer_id}</span>
          ) : (
            'Sin Stripe'
          )}
        </span>
      </div>
    </div>
  )
}
